import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DashboardLayout } from "@/components/Dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, ExternalLink, Loader2 } from "lucide-react";
import { api } from "@/lib/apiClient";

type SearchResults = {
  workOrders: any[];
  customers: any[];
  projects: any[];
  productModels: any[];
  molds: any[];
  machines: any[];
  materialInventory: any[];
  rawMaterials: any[];
  tools: any[];
  productionRecipes: any[];
};

const EMPTY_RESULTS: SearchResults = {
  workOrders: [],
  customers: [],
  projects: [],
  productModels: [],
  molds: [],
  machines: [],
  materialInventory: [],
  rawMaterials: [],
  tools: [],
  productionRecipes: [],
};

const RESULT_LIMIT = 6;

const getCustomerName = (customer: any) => {
  if (!customer) return "";
  const company = customer.company_name || "";
  const person = [customer.first_name, customer.last_name].filter(Boolean).join(" ").trim();
  return company || person;
};

const GlobalSearch = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResults>(EMPTY_RESULTS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runSearch = async (term: string) => {
    const trimmed = term.trim();
    if (!trimmed) {
      setResults(EMPTY_RESULTS);
      return;
    }

    const pattern = `*${trimmed}*`;
    setLoading(true);
    setError(null);

    try {
      const customersPromise = api
        .from("customers")
        .select("id, first_name, last_name, company_name, email, phone, is_active")
        .or([
          `first_name.ilike.${pattern}`,
          `last_name.ilike.${pattern}`,
          `company_name.ilike.${pattern}`,
          `email.ilike.${pattern}`,
          `phone.ilike.${pattern}`,
        ])
        .order("created_at", { ascending: false })
        .limit(RESULT_LIMIT);

      const projectsPromise = api
        .from("projects")
        .select("id, name, reference_code, status")
        .or([
          `name.ilike.${pattern}`,
          `reference_code.ilike.${pattern}`,
          `description.ilike.${pattern}`,
        ])
        .order("created_at", { ascending: false })
        .limit(RESULT_LIMIT);

      const productModelsPromise = api
        .from("product_models")
        .select("id, name, reference, description")
        .or([
          `name.ilike.${pattern}`,
          `reference.ilike.${pattern}`,
          `description.ilike.${pattern}`,
        ])
        .order("created_at", { ascending: false })
        .limit(RESULT_LIMIT);

      const moldsPromise = api
        .from("molds")
        .select("id, name, code, material, location, status")
        .or([
          `name.ilike.${pattern}`,
          `code.ilike.${pattern}`,
          `material.ilike.${pattern}`,
          `location.ilike.${pattern}`,
        ])
        .order("created_at", { ascending: false })
        .limit(RESULT_LIMIT);

      const machinesPromise = api
        .from("machines")
        .select("id, name, model, type")
        .or([
          `name.ilike.${pattern}`,
          `model.ilike.${pattern}`,
          `type.ilike.${pattern}`,
        ])
        .order("created_at", { ascending: false })
        .limit(RESULT_LIMIT);

      const materialInventoryPromise = api
        .from("material_inventory")
        .select("id, name, code, category, location")
        .or([
          `name.ilike.${pattern}`,
          `code.ilike.${pattern}`,
          `category.ilike.${pattern}`,
          `location.ilike.${pattern}`,
        ])
        .order("created_at", { ascending: false })
        .limit(RESULT_LIMIT);

      const rawMaterialsPromise = api
        .from("raw_materials")
        .select("id, name, code, category, batch_number, supplier")
        .or([
          `name.ilike.${pattern}`,
          `code.ilike.${pattern}`,
          `category.ilike.${pattern}`,
          `batch_number.ilike.${pattern}`,
          `supplier.ilike.${pattern}`,
        ])
        .order("created_at", { ascending: false })
        .limit(RESULT_LIMIT);

      const toolsPromise = api
        .from("tools")
        .select("id, name, code, type, location, status")
        .or([
          `name.ilike.${pattern}`,
          `code.ilike.${pattern}`,
          `type.ilike.${pattern}`,
          `location.ilike.${pattern}`,
        ])
        .order("created_at", { ascending: false })
        .limit(RESULT_LIMIT);

      const recipesPromise = api
        .from("production_recipes")
        .select("id, name, description")
        .or([`name.ilike.${pattern}`, `description.ilike.${pattern}`])
        .order("created_at", { ascending: false })
        .limit(RESULT_LIMIT);

      const customersResult = await customersPromise;
      const customerIds = (customersResult.data || [])
        .map((customer: any) => customer.id)
        .filter(Boolean);

      const workOrderFilters = [
        `order_number.ilike.${pattern}`,
        `product_name.ilike.${pattern}`,
      ];
      if (customerIds.length > 0) {
        workOrderFilters.push(`customer_id.in.(${customerIds.join(",")})`);
      }

      const workOrdersPromise = api
        .from("work_orders")
        .select(
          "id, order_number, product_name, status, customer_id, customers(first_name, last_name, company_name)"
        )
        .or(workOrderFilters)
        .order("created_at", { ascending: false })
        .limit(RESULT_LIMIT);

      const [
        workOrders,
        projects,
        productModels,
        molds,
        machines,
        materialInventory,
        rawMaterials,
        tools,
        productionRecipes,
      ] = await Promise.all([
        workOrdersPromise,
        projectsPromise,
        productModelsPromise,
        moldsPromise,
        machinesPromise,
        materialInventoryPromise,
        rawMaterialsPromise,
        toolsPromise,
        recipesPromise,
      ]);

      setResults({
        workOrders: workOrders.data || [],
        customers: customersResult.data || [],
        projects: projects.data || [],
        productModels: productModels.data || [],
        molds: molds.data || [],
        machines: machines.data || [],
        materialInventory: materialInventory.data || [],
        rawMaterials: rawMaterials.data || [],
        tools: tools.data || [],
        productionRecipes: productionRecipes.data || [],
      });
    } catch (err: any) {
      setError(err?.message || "Error al buscar resultados");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initialQuery = searchParams.get("q") || "";
    setQuery(initialQuery);
    if (initialQuery.trim()) {
      runSearch(initialQuery);
    } else {
      setResults(EMPTY_RESULTS);
    }
  }, [searchParams]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = query.trim();
    if (trimmed) {
      setSearchParams({ q: trimmed });
    } else {
      setSearchParams({});
    }
  };

  const getStatusBadge = (status?: string) => {
    if (!status) return null;
    const className =
      status === "completed"
        ? "bg-green-500/10 text-green-500 border-green-500/20"
        : status === "in_progress"
        ? "bg-blue-500/10 text-blue-500 border-blue-500/20"
        : status === "cancelled"
        ? "bg-red-500/10 text-red-500 border-red-500/20"
        : "bg-yellow-500/10 text-yellow-500 border-yellow-500/20";
    return <Badge className={className}>{status}</Badge>;
  };

  const renderEmpty = (label: string) => (
    <p className="text-sm text-muted-foreground">Sin resultados en {label}.</p>
  );

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-2">
          <h1 className="text-3xl font-bold">Busqueda global</h1>
          <p className="text-sm text-muted-foreground">
            Busca en ordenes, clientes, proyectos, inventario y mas.
          </p>
        </div>

        <Card>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nombre, codigo, referencia..."
                  className="pl-9"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                />
              </div>
              <Button type="submit" className="gap-2" disabled={loading}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                Buscar
              </Button>
            </form>
            {error && (
              <p className="mt-3 text-sm text-destructive">{error}</p>
            )}
          </CardContent>
        </Card>

        {loading ? (
          <Card>
            <CardContent className="flex items-center gap-3 py-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Buscando resultados...
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Ordenes de trabajo</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {results.workOrders.length === 0 && renderEmpty("ordenes de trabajo")}
                {results.workOrders.map((order: any) => {
                  const customer = Array.isArray(order.customers)
                    ? order.customers[0]
                    : order.customers;
                  const customerName = getCustomerName(customer);
                  return (
                    <div key={order.id} className="flex items-center justify-between gap-3 border-b pb-2 last:border-b-0 last:pb-0">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{order.order_number}</p>
                          {getStatusBadge(order.status)}
                        </div>
                        <p className="text-xs text-muted-foreground">{order.product_name}</p>
                        {customerName && (
                          <p className="text-xs text-muted-foreground">Cliente: {customerName}</p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/work-orders?open=${order.id}`)}
                        className="gap-2"
                      >
                        <ExternalLink className="h-4 w-4" />
                        Ver
                      </Button>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Clientes</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {results.customers.length === 0 && renderEmpty("clientes")}
                {results.customers.map((customer: any) => {
                  const name = getCustomerName(customer) || "Cliente sin nombre";
                  return (
                    <div key={customer.id} className="flex items-center justify-between gap-3 border-b pb-2 last:border-b-0 last:pb-0">
                      <div>
                        <p className="font-medium">{name}</p>
                        <p className="text-xs text-muted-foreground">
                          {customer.email || customer.phone || "Sin contacto"}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/customers/${customer.id}`)}
                        className="gap-2"
                      >
                        <ExternalLink className="h-4 w-4" />
                        Ver
                      </Button>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Proyectos</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {results.projects.length === 0 && renderEmpty("proyectos")}
                {results.projects.map((project: any) => (
                  <div key={project.id} className="flex items-center justify-between gap-3 border-b pb-2 last:border-b-0 last:pb-0">
                    <div>
                      <p className="font-medium">{project.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {project.reference_code || "Sin referencia"}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate("/projects")}
                      className="gap-2"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Ver
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Moldes</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {results.molds.length === 0 && renderEmpty("moldes")}
                {results.molds.map((mold: any) => (
                  <div key={mold.id} className="flex items-center justify-between gap-3 border-b pb-2 last:border-b-0 last:pb-0">
                    <div>
                      <p className="font-medium">{mold.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {mold.code || mold.material || "Sin datos"}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate("/molds")}
                      className="gap-2"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Ver
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Maquinas</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {results.machines.length === 0 && renderEmpty("maquinas")}
                {results.machines.map((machine: any) => (
                  <div key={machine.id} className="flex items-center justify-between gap-3 border-b pb-2 last:border-b-0 last:pb-0">
                    <div>
                      <p className="font-medium">{machine.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {machine.model || machine.type || "Sin datos"}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate("/machines")}
                      className="gap-2"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Ver
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Inventario</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {results.materialInventory.length === 0 && renderEmpty("inventario")}
                {results.materialInventory.map((item: any) => (
                  <div key={item.id} className="flex items-center justify-between gap-3 border-b pb-2 last:border-b-0 last:pb-0">
                    <div>
                      <p className="font-medium">{item.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {item.code || item.category || "Sin datos"}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate("/materials?tab=inventory")}
                      className="gap-2"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Ver
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Materias primas</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {results.rawMaterials.length === 0 && renderEmpty("materias primas")}
                {results.rawMaterials.map((item: any) => (
                  <div key={item.id} className="flex items-center justify-between gap-3 border-b pb-2 last:border-b-0 last:pb-0">
                    <div>
                      <p className="font-medium">{item.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {item.code || item.batch_number || "Sin datos"}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate("/materials?tab=raw")}
                      className="gap-2"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Ver
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Herramientas</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {results.tools.length === 0 && renderEmpty("herramientas")}
                {results.tools.map((tool: any) => (
                  <div key={tool.id} className="flex items-center justify-between gap-3 border-b pb-2 last:border-b-0 last:pb-0">
                    <div>
                      <p className="font-medium">{tool.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {tool.code || tool.type || "Sin datos"}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate("/tools")}
                      className="gap-2"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Ver
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Recetas</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {results.productionRecipes.length === 0 && renderEmpty("recetas")}
                {results.productionRecipes.map((recipe: any) => (
                  <div key={recipe.id} className="flex items-center justify-between gap-3 border-b pb-2 last:border-b-0 last:pb-0">
                    <div>
                      <p className="font-medium">{recipe.name}</p>
                      <p className="text-xs text-muted-foreground line-clamp-1">
                        {recipe.description || "Sin descripcion"}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate("/recipes")}
                      className="gap-2"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Ver
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default GlobalSearch;
